import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Helmet } from 'react-helmet-async'; // ADDED: Helmet import

// Initialize the Supabase client configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseAnonKey);

export default function SeoDirectory() {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UTILITY: Cleans database strings (removes hyphens, capitalizes words)
  const formatKeyword = (keyword) => {
    if (!keyword) return '';
    return keyword
      .replace(/-/g, ' ')
      .replace(/\b\w/g, char => char.toUpperCase());
  };

  useEffect(() => {
    // REMOVED: Manual document.title and document.head manipulation

    async function fetchSeoLinks() {
      try {
        setLoading(true);
        
        // Pulls the slug and master_keyword from the targeted seo_landing_pages table
        const { data, error: supabaseError } = await supabase
          .from('seo_landing_pages')
          .select('slug, master_keyword')
          .eq('status', 'published')
          .order('master_keyword', { ascending: true });

        if (supabaseError) throw supabaseError;

        setLinks(data || []);
      } catch (err) {
        console.error('Error loading directory links:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    fetchSeoLinks();
  }, []);

  if (loading) {
    return (
      <div className="p-10 text-center font-sans text-slate-600">
        <p>Loading sourcing directory...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-10 text-center font-sans text-red-600">
        <p>Error loading directory: {error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 font-sans">
      {/* ADDED: Dynamic Helmet SEO block */}
      <Helmet>
        <title>Industrial Building Material Sourcing Directories | TariffBeater</title>
        <meta 
          name="description" 
          content="Explore localized ICI building sectors, structural variants, and commercial supply channels across Canada. Find verified Canadian material suppliers." 
        />
        <link rel="canonical" href="https://www.tariffbeater.com/directory" />
      </Helmet>

      <header className="mb-10 border-b border-slate-200 pb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-3">
          Industrial Sourcing Directories
        </h1>
        <p className="text-lg text-slate-600">
          Explore localized ICI building sectors, structural variants, and commercial supply channels across Canada.
        </p>
      </header>

      {/* --- PRIMARY PLATFORM PORTALS --- */}
      <section className="mb-12">
        <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
          Primary Market Portals
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Link 
            to="/suppliers" 
            className="p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all group"
          >
            <h3 className="text-lg font-bold text-blue-600 group-hover:text-blue-700 mb-2">
              For Canadian Fabricators →
            </h3>
            <p className="text-slate-600 text-sm">
              List your structural and envelope inventory. Connect directly with ICI General Contractors and bypass distributor markups.
            </p>
          </Link>

          <Link 
            to="/buyers" 
            className="p-6 bg-white border border-slate-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all group"
          >
            <h3 className="text-lg font-bold text-blue-600 group-hover:text-blue-700 mb-2">
              For General Contractors & Estimators →
            </h3>
            <p className="text-slate-600 text-sm">
              Access the private directory to source pre-verified domestic materials, ensure CFTA compliance, and calculate landed costs.
            </p>
          </Link>
        </div>
      </section>

      {/* --- DYNAMIC SEO DIRECTORIES --- */}
      <section>
        <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
          <span className="w-2 h-2 bg-slate-400 rounded-full"></span>
          Material Categories
        </h2>
        
        {links.length === 0 ? (
          <p className="text-slate-500 italic">No material directories published yet.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {links.map((page) => (
              <div 
                key={page.slug} 
                className="p-4 bg-white border border-slate-200 rounded-lg hover:border-slate-300 hover:shadow-sm transition-all"
              >
                <Link 
                  to={`/materials/${page.slug}`} 
                  className="text-slate-700 hover:text-blue-600 font-medium block text-sm capitalize"
                >
                  {formatKeyword(page.master_keyword)}
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}